'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Alert01Icon, Loading03Icon, StopIcon } from '@terra/ui-icons';
import { Box, HStack, token } from '@terra/ui-styled-system';
import { Badge } from '../../components/badge';
import { Dialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';
import { MetaDefault } from '../../components/typography';
import { Tooltip } from '../../components/tooltip';

type RetestCompletedResult = 'success' | 'fail' | null;

export type FindingRetestAbortControls = {
  isShowingAbortedChip: boolean;
  isPending: boolean;
  onConfirm: () => void | Promise<void>;
  onRunAgain: () => void;
};

type FindingRetestStatusBadgeProps = {
  isRetesting: boolean;
  retestCompletedResult?: RetestCompletedResult;
  onViewActiveRetestTrace?: () => void;
  abort?: FindingRetestAbortControls;
};

function ClickableStatusBadge({
  label,
  icon,
  color,
  borderColor,
  tooltip,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  color: string;
  borderColor: string;
  tooltip: string;
  onClick?: () => void;
}) {
  const badge = (
    <Badge
      variant="outline"
      size="md"
      icon={icon}
      color={color}
      borderColor={borderColor}
      fontWeight="medium"
      cursor={onClick ? 'pointer' : 'default'}
      _hover={onClick ? { bg: 'bg.subtle' } : undefined}
    >
      {label}
    </Badge>
  );

  return (
    <Tooltip title={tooltip} positioning={{ strategy: 'fixed' }}>
      {onClick ? (
        <Box
          as="button"
          aria-label={label}
          onClick={onClick}
          display="inline-flex"
          bg="transparent"
          border="none"
          p={0}
          m={0}
          cursor="pointer"
        >
          {badge}
        </Box>
      ) : (
        badge
      )}
    </Tooltip>
  );
}

function RetestingChip({
  label,
  tooltip,
  abortTooltip,
  onViewActiveRetestTrace,
  onOpenAbortConfirm,
}: {
  label: string;
  tooltip: string;
  abortTooltip: string;
  onViewActiveRetestTrace?: () => void;
  onOpenAbortConfirm?: () => void;
}) {
  return (
    <HStack
      gap={0}
      alignItems="stretch"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.accent.subtle"
      borderRadius="l2"
      overflow="hidden"
      bg="bg.default"
    >
      <Tooltip title={tooltip} positioning={{ strategy: 'fixed' }}>
        <Box
          as={onViewActiveRetestTrace ? 'button' : 'div'}
          aria-label={onViewActiveRetestTrace ? label : undefined}
          onClick={onViewActiveRetestTrace}
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          px={2}
          py={1}
          bg="transparent"
          border="none"
          cursor={onViewActiveRetestTrace ? 'pointer' : 'default'}
          _hover={onViewActiveRetestTrace ? { bg: 'bg.subtle' } : undefined}
        >
          <Box animation="spin 1s linear infinite" display="flex">
            <Loading03Icon size={12} color={token('colors.fg.accent.subtle')} />
          </Box>
          <MetaDefault
            fontWeight="medium"
            color="fg.default"
            whiteSpace="nowrap"
          >
            {label}
          </MetaDefault>
        </Box>
      </Tooltip>
      {onOpenAbortConfirm ? (
        <>
          <Box w="1px" alignSelf="stretch" bg="border.accent.subtle" />
          <Tooltip title={abortTooltip} positioning={{ strategy: 'fixed' }}>
            <IconButton
              type="button"
              variant="ghost"
              size="xs"
              aria-label={abortTooltip}
              onClick={onOpenAbortConfirm}
              borderRadius="0"
              h="auto"
              minW="auto"
              px={1.5}
            >
              <StopIcon size={12} color={token('colors.fg.default')} />
            </IconButton>
          </Tooltip>
        </>
      ) : null}
    </HStack>
  );
}

export function FindingRetestStatusBadge({
  isRetesting,
  retestCompletedResult,
  onViewActiveRetestTrace,
  abort,
}: FindingRetestStatusBadgeProps) {
  const t = useTranslations('designSystem.findings.findingDrawer');
  const [isAbortConfirmOpen, setAbortConfirmOpen] = useState(false);
  const [isAbortConfirmLoading, setIsAbortConfirmLoading] = useState(false);
  const isShowingAbortedChip = abort?.isShowingAbortedChip === true;
  const isAbortConfirmBusy = abort?.isPending === true || isAbortConfirmLoading;

  useEffect(() => {
    if (!isAbortConfirmOpen) {
      setIsAbortConfirmLoading(false);
    }
  }, [isAbortConfirmOpen]);

  const statusChip = isShowingAbortedChip ? (
    <ClickableStatusBadge
      label={t('retest.badgeAborted')}
      icon={<StopIcon size={12} color={token('colors.fg.accent.subtle')} />}
      color="fg.default"
      borderColor={token('colors.border.accent.subtle')}
      tooltip={t('retest.badgeAbortedTooltip')}
      onClick={abort?.onRunAgain}
    />
  ) : isRetesting ? (
    <RetestingChip
      label={t('retest.badgeInProgress')}
      tooltip={
        onViewActiveRetestTrace
          ? t('retest.badgeInProgressTooltip')
          : t('retest.badgeInProgress')
      }
      abortTooltip={t('retest.abortTooltip')}
      onViewActiveRetestTrace={onViewActiveRetestTrace}
      onOpenAbortConfirm={abort ? () => setAbortConfirmOpen(true) : undefined}
    />
  ) : retestCompletedResult === 'fail' ? (
    <ClickableStatusBadge
      label={t('retest.badgeFailed')}
      icon={<Alert01Icon size={12} color={token('colors.fg.error')} />}
      color="fg.error"
      borderColor={token('colors.border.error')}
      tooltip={
        onViewActiveRetestTrace
          ? t('retest.badgeFailedTooltip')
          : t('retest.badgeFailed')
      }
      onClick={onViewActiveRetestTrace}
    />
  ) : null;

  if (!statusChip && !abort) {
    return null;
  }

  return (
    <>
      {statusChip}
      {abort ? (
        <Dialog
          open={isAbortConfirmOpen}
          onOpenChange={setAbortConfirmOpen}
          lazyMount
          unmountOnExit
          size="sm"
          title={t('retest.abortConfirmTitle')}
          description={t('retest.abortConfirmDescription')}
          secondaryAction={{
            label: t('retest.abortConfirmKeepRunning'),
            onClick: () => setAbortConfirmOpen(false),
            disabled: isAbortConfirmBusy,
          }}
          primaryAction={{
            label: t('retest.abortConfirmAbort'),
            loading: isAbortConfirmBusy,
            onClick: async () => {
              setIsAbortConfirmLoading(true);

              try {
                await abort.onConfirm();
                setAbortConfirmOpen(false);
              } catch {
                setIsAbortConfirmLoading(false);
              }
            },
          }}
        >
          {null}
        </Dialog>
      ) : null}
    </>
  );
}
